import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api";
import * as Profile from "@/components/auth/Profile";
import { AlertDialog } from "@/components/dialog/alert/AlertDialog";
import { ConfirmDialog } from "@/components/dialog/confirm/ConfirmDialog";
import { TeamItem } from "@/components/doc/left-sidebar/team-dropdown/TeamDropdown";
import { Icon } from "@/components/icons/Icon";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type { IconName } from "@/components/icons/Icon";
import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { TeamModel } from "@/models/TeamModel";
import type { TeamDialogTab } from "@/store/dialog/TeamDialogStore";

const TeamTitleDropdown = observer(function TeamTitleDropdown({ team }: { team: TeamModel }) {
  const t = useTranslations();
  const { dialog, teams } = useStore();

  const items: DropdownItem[] = teams.sortedUserTeams.map((userTeam) => ({
    title: userTeam.title || t("teams.unnamed"),
    component: (
      <TeamItem
        team={userTeam}
        onClick={() => dialog.teamDialog.selectTeam(userTeam)}
        className={twMerge("pr-0", team.uid === userTeam.uid && "bg-blue-50 dark:bg-blue-950")}
      />
    ),
  }));

  return (
    <Dropdown
      anchor={{ to: "bottom start", padding: "10px" }}
      className="flex w-full"
      itemsClassName="fromSm:!max-w-[500px]"
      toggleButtonLabel={t("teamDialog.selectTeam")}
      toggleButtonComponent={
        <div
          className={twMerge(
            "flex items-center min-w-0 w-full -mx-1 px-1 rounded-6 cursor-pointer",
            "hover:bg-sidebar-button-hover-bg sm:justify-center sm:hover:bg-transparent",
          )}
        >
          <span
            className={twMerge(
              "line-clamp-3 wrap-anywhere text-left text-[17px] leading-[21px] font-semibold tracking-tight",
              "sm:text-center",
            )}
          >
            {team.title}
          </span>
          <Icon name="ArrowDownLarge" width={16} height={16} className="ml-1 shrink-0" />
        </div>
      }
      items={items}
    />
  );
});

const TeamDialogSidebarHeader = observer(function TeamDialogSidebarHeader() {
  const { team } = useStore().dialog.teamDialog;

  if (!team) return null;

  return (
    <div className={twMerge("flex flex-col mb-5 px-1.25 pt-2.5", "sm:p-4 sm:pb-0 sm:items-center")}>
      <Profile.Userpic
        src={team.avatarUrls?.[120]}
        name={team.title || ""}
        setColorFrom={team.uid}
        className={twMerge("mb-3 text-[30px] w-[60px] h-[60px]", "sm:w-16 sm:h-16 sm:text-16")}
      />
      <div className="flex flex-1 flex-col min-w-0 w-full">
        <TeamTitleDropdown team={team} />
        <div
          className={twMerge(
            "max-w-full text-gray-500 text-[15px] leading-[18px] tracking-tight wrap-anywhere",
            "sm:text-center",
          )}
        >
          @{team.permalink}
        </div>
      </div>
    </div>
  );
});

type SidebarItem = {
  title: string;
  icon?: IconName;
  onClick: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
  className?: string;
};

const TeamDialogSidebarBody = observer(function TeamDialogSidebarBody() {
  const t = useTranslations();
  const { dialog, teams } = useStore();
  const { teamDialog } = dialog;
  const { team, availableTabs } = teamDialog;
  const [leaveTeamConfirmIsOpen, setLeaveTeamConfirmIsOpen] = useState(false);
  const [leaveTeamInProcess, setLeaveTeamInProcess] = useState(false);
  const [leaveTeamAlertIsOpen, setLeaveTeamAlertIsOpen] = useState(false);
  const [leaveTeamError, setLeaveTeamError] = useState("");

  const tabs: Record<TeamDialogTab, { title: string; icon: IconName }> = {
    account: { title: t("teamDialog.account"), icon: "Profile" },
    settings: { title: t("teamDialog.settings"), icon: "Settings" },
    members: { title: t("teamDialog.members"), icon: "Users" },
    billing: { title: t("teamDialog.planBilling"), icon: "PlanBilling" },
  };

  const items: SidebarItem[] = [
    ...availableTabs.map((tab) => ({
      ...tabs[tab],
      onClick: () => teamDialog.setActiveTab(tab),
      isActive: teamDialog.activeTab === tab,
    })),
    ...(team && availableTabs.includes("billing")
      ? [
          {
            title: t("teamDialog.billing.upgrade"),
            icon: "chat-icons/ArrowUp",
            className:
              "text-blue-500 [--color-icon-main:var(--color-icon-active)] [--color-icon-secondary:var(--color-icon-active)]",
            onClick: () => dialog.upgradePlanDialog.open(team),
          } satisfies SidebarItem,
        ]
      : []),
    ...(team && !team.personal
      ? [
          {
            title: t("teamDialog.leaveTeam"),
            className: "mt-auto",
            onClick: () => setLeaveTeamConfirmIsOpen(true),
          },
        ]
      : []),
  ];

  const handleTeamLeave = async () => {
    setLeaveTeamInProcess(true);

    try {
      await teamDialog.team?.leave();
      teams.setCurrentTeam(teams.personalTeam);
      teams.loadTeams();
      teamDialog.close();
    } catch (err) {
      setLeaveTeamAlertIsOpen(true);
      setLeaveTeamError(getApiErrorMessage(err));
    }

    setLeaveTeamInProcess(false);
    setLeaveTeamConfirmIsOpen(false);
  };

  return (
    <>
      <div className={twMerge("flex flex-col h-full mb-2.5", "sm:my-auto")}>
        {items.map((item) => {
          return (
            <button
              key={item.title}
              type="button"
              className={twMerge(
                "flex items-center rounded-6 min-h-8.5 p-1.25",
                item.isActive && "bg-blue-100 dark:bg-blue-900",
                item.isDisabled && "opacity-50",
                item.className,
                !item.isActive && !item.isDisabled && "hover:bg-blue-50 hover:dark:bg-blue-950",
              )}
              onClick={item.onClick}
              disabled={item.isDisabled}
            >
              {item.icon && (
                <Icon name={item.icon} className="w-[24px] h-[24px] sm:w-[28px] sm:h-[28px]" />
              )}
              <div className="flex flex-1 ml-1 text-[15px] leading-[18px] tracking-tight">
                {item.title}
              </div>
            </button>
          );
        })}
      </div>
      <ConfirmDialog
        isOpen={leaveTeamConfirmIsOpen}
        isLoading={leaveTeamInProcess}
        title={t("teamDialog.leaveTeamConfirmation", { team: teamDialog.team?.title })}
        onConfirm={handleTeamLeave}
        onClose={() => setLeaveTeamConfirmIsOpen(false)}
      />
      <AlertDialog
        isOpen={leaveTeamAlertIsOpen}
        title={t("teamDialog.leaveTeamError")}
        description={leaveTeamError}
        onClose={() => {
          setLeaveTeamAlertIsOpen(false);
          setLeaveTeamError("");
        }}
      />
    </>
  );
});

export function TeamDialogSidebar() {
  return (
    <div
      className={twMerge(
        "flex flex-col overflow-y-auto no-scrollbar w-full max-w-[250px]",
        "sm:max-w-none sm:overflow-visible",
      )}
    >
      <TeamDialogSidebarHeader />
      <TeamDialogSidebarBody />
    </div>
  );
}
